import React, { useEffect, useMemo, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import { ShareRow } from './components/ShareRow';
import {
  loadUserShares,
  manageShare,
  sendShareReminder,
  selectShares,
  selectSharingLoading,
} from './sharingSlice';
import type { SharedByMe, SharedWithMe } from '../../api/sharingTypes';
import Toast from 'react-native-toast-message';

/**
 * MySharesScreen — Sprint 8 Phase C.
 *
 * Dedicated deep-dive for every share the user has on the platform.
 * Two tabs:
 *   - Received → `shared_with_me` from /users/shares. Anything I've
 *     been offered, regardless of status. Pending shows the claim
 *     CTA in the row's pill; claimed/returned/declined are
 *     informational rows.
 *   - Sent → `shared_by_me`. Same shape, but from the owner's view.
 *
 * Reached via:
 *   - Share tab's "Available to claim" / "Shares I sent" /
 *     "Currently with me" section headers ([View all →])
 *   - Direct deep-link (future: /my-shares)
 *
 * Route param `tab` (optional) selects the initial tab — passed by
 * the CrewHubScreen handler so the user lands on the right one.
 */

type TabKey = 'received' | 'sent';

export const MySharesScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const dispatch = useAppDispatch();

  const shares = useAppSelector(selectShares);
  const sharingLoading = useAppSelector(selectSharingLoading);
  const isLoading = sharingLoading.shares;

  const initialTab: TabKey =
    route.params?.tab === 'sent' ? 'sent' : 'received';
  const [tab, setTab] = useState<TabKey>(initialTab);

  useEffect(() => {
    dispatch(loadUserShares({} as any));
  }, [dispatch]);

  const received = shares?.shared_with_me ?? [];
  const sent = shares?.shared_by_me ?? [];

  // Status filter chips — null = all, otherwise filter to the
  // matching status. Default state shows everything in the tab.
  const [statusFilter, setStatusFilter] = useState<
    null | SharedByMe['status']
  >(null);

  // Reset the filter chip when the tab flips (sent / received have
  // different status mixes; "Declined" on sent doesn't apply to
  // received, etc.).
  useEffect(() => setStatusFilter(null), [tab]);

  const visible = useMemo(() => {
    const source: Array<SharedByMe | SharedWithMe> =
      tab === 'received' ? received : sent;
    if (!statusFilter) return source;
    return source.filter((s) => s.status === statusFilter);
  }, [tab, received, sent, statusFilter]);

  // PR12 — "Awaiting your confirmation" section. Outbound shares
  // in RETURNED status are borrowers who marked the item returned
  // but the lender hasn't confirmed receipt. Render them in a
  // dedicated section at the top of the Sent tab so the lender
  // can close the loop in 1 tap. (The email's "Confirm Receipt"
  // button does the same thing — this is the in-app counterpart.)
  const pendingConfirmations = useMemo(
    () => sent.filter((s) => s.status === 'RETURNED'),
    [sent],
  );
  const [confirmingShareId, setConfirmingShareId] = useState<number | null>(
    null,
  );
  const [remindingShareId, setRemindingShareId] = useState<number | null>(null);
  const [returningShareId, setReturningShareId] = useState<number | null>(null);
  const handleConfirm = async (shareId: number, itemName: string) => {
    setConfirmingShareId(shareId);
    try {
      const result = await dispatch(
        manageShare({ shareId, request: { action: 'COMPLETE' } }) as any,
      );
      if (manageShare.fulfilled.match(result)) {
        Toast.show({
          type: 'success',
          text1: 'Receipt confirmed',
          text2: `${itemName} share closed.`,
        });
      } else {
        Toast.show({
          type: 'error',
          text1: "Couldn't confirm receipt",
          text2:
            ((result.payload as any)?.data?.detail as string) ??
            'Please try again.',
        });
      }
    } finally {
      setConfirmingShareId(null);
    }
  };

  // C4 — owner nudges an external-share recipient who hasn't returned yet
  // (outbound · CLAIMED). Backend rate-limits to one per 24h → surface 429
  // as a friendly "already reminded" rather than an error.
  const handleRemind = async (shareId: number, itemName: string) => {
    setRemindingShareId(shareId);
    try {
      const result = await dispatch(sendShareReminder(shareId) as any);
      if (sendShareReminder.fulfilled.match(result)) {
        Toast.show({
          type: 'success',
          text1: 'Reminder sent',
          text2: `We nudged the borrower of ${itemName}.`,
        });
      } else {
        const status = (result.payload as any)?.status;
        Toast.show({
          type: status === 429 ? 'info' : 'error',
          text1:
            status === 429
              ? 'Already reminded recently'
              : "Couldn't send reminder",
          text2:
            status === 429
              ? 'You can remind once every 24 hours.'
              : (((result.payload as any)?.data?.detail as string) ??
                'Please try again.'),
        });
      }
    } finally {
      setRemindingShareId(null);
    }
  };

  // C5 — claimer marks an external borrow as returned (inbound · CLAIMED).
  // This flips the share to RETURNED; the owner then confirms receipt
  // (handleConfirm → COMPLETE). Refresh so the row updates immediately.
  const handleReturn = async (shareId: number, itemName: string) => {
    setReturningShareId(shareId);
    try {
      const result = await dispatch(
        manageShare({ shareId, request: { action: 'RETURN' } }) as any,
      );
      if (manageShare.fulfilled.match(result)) {
        Toast.show({
          type: 'success',
          text1: 'Marked as returned',
          text2: `${itemName} — the owner will confirm receipt.`,
        });
        dispatch(loadUserShares({} as any));
      } else {
        Toast.show({
          type: 'error',
          text1: "Couldn't mark returned",
          text2:
            ((result.payload as any)?.data?.detail as string) ??
            'Please try again.',
        });
      }
    } finally {
      setReturningShareId(null);
    }
  };

  const handleRefresh = () => {
    dispatch(loadUserShares({} as any));
  };

  const goShareClaim = (shareUrl: string | undefined) => {
    const m = (shareUrl ?? '').match(/\/share\/([^/?#]+)/);
    if (m) navigation.navigate('ShareClaim', { shareToken: m[1] });
  };

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <Pressable
          onPress={() => goBackOrHome(navigation)}
          style={styles.backBtn}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <MaterialIcons
            name="arrow-back"
            size={22}
            color={fpColors.onSurface}
          />
        </Pressable>
        <Text style={styles.title}>My shares</Text>
        <View style={{ width: 36 }} />
      </View>

      {/* Tab switcher */}
      <View style={styles.tabRow} accessibilityRole="tablist">
        <TabButton
          /* FE-205 — "Received" → "Borrowed" to match the user mental
             model. Same data; just reads more naturally. */
          label={`Borrowed${received.length > 0 ? ` (${received.length})` : ''}`}
          active={tab === 'received'}
          onPress={() => setTab('received')}
          testID="my-shares-tab-received"
        />
        <TabButton
          /* FE-205 — "Sent" → "Lent Out" — symmetric with Borrowed. */
          label={`Lent Out${sent.length > 0 ? ` (${sent.length})` : ''}`}
          active={tab === 'sent'}
          onPress={() => setTab('sent')}
          testID="my-shares-tab-sent"
        />
      </View>

      {/* Status filter chip row.
          PR19 — was a horizontal ScrollView with no explicit height,
          which collapses to 0px on React Native Web. Result: chips
          invisible on the entire MyShares screen. Replaced with a
          plain View (4-5 chips fit comfortably on every screen width;
          flexWrap lets it gracefully fall to two rows on narrow
          viewports). */}
      <View style={styles.chipRow}>
        <FilterChip
          label="All"
          active={statusFilter === null}
          onPress={() => setStatusFilter(null)}
        />
        {tab === 'received' ? (
          <>
            <FilterChip
              label="Pending"
              active={statusFilter === 'PENDING'}
              onPress={() => setStatusFilter('PENDING')}
            />
            <FilterChip
              label="Claimed"
              active={statusFilter === 'CLAIMED'}
              onPress={() => setStatusFilter('CLAIMED')}
            />
            <FilterChip
              label="Returned"
              active={statusFilter === 'RETURNED'}
              onPress={() => setStatusFilter('RETURNED')}
            />
          </>
        ) : (
          <>
            <FilterChip
              label="Pending"
              active={statusFilter === 'PENDING'}
              onPress={() => setStatusFilter('PENDING')}
            />
            <FilterChip
              label="Claimed"
              active={statusFilter === 'CLAIMED'}
              onPress={() => setStatusFilter('CLAIMED')}
            />
            <FilterChip
              label="Returned"
              active={statusFilter === 'RETURNED'}
              onPress={() => setStatusFilter('RETURNED')}
            />
            <FilterChip
              label="Cancelled"
              active={statusFilter === 'CANCELLED'}
              onPress={() => setStatusFilter('CANCELLED')}
            />
          </>
        )}
      </View>

      <ScrollView
        refreshControl={
          <RefreshControl refreshing={isLoading} onRefresh={handleRefresh} />
        }
        contentContainerStyle={styles.list}
      >
        {/* PR12 — Awaiting your confirmation (Sent tab only). One row
            per RETURNED outbound share with an inline Confirm receipt
            CTA. Skipped when nothing is awaiting confirmation. */}
        {tab === 'sent' && pendingConfirmations.length > 0 ? (
          <View style={styles.pendingSection}>
            <View style={styles.pendingHeader}>
              <MaterialIcons
                name="mark-email-read"
                size={16}
                color={fpColors.tertiary}
              />
              <Text style={styles.pendingTitle}>
                Awaiting your confirmation ({pendingConfirmations.length})
              </Text>
            </View>
            <Text style={styles.pendingSubtitle}>
              Borrowers marked these returned. Tap to close the loop.
            </Text>
            <View style={styles.pendingList}>
              {pendingConfirmations.map((s) => {
                const counterpartyName =
                  (s as SharedByMe).shared_with
                    ? (s as SharedByMe).shared_with!.split('(')[0].trim()
                    : null;
                const isBusy = confirmingShareId === s.share_id;
                return (
                  <View
                    key={s.share_id}
                    style={styles.pendingRow}
                    testID={`pending-confirm-row-${s.share_id}`}
                  >
                    <View style={styles.pendingRowText}>
                      <Text style={styles.pendingItemName} numberOfLines={1}>
                        {s.item_name}
                      </Text>
                      <Text style={styles.pendingItemSub} numberOfLines={1}>
                        {counterpartyName
                          ? `Returned by ${counterpartyName}`
                          : 'Marked returned'}
                      </Text>
                    </View>
                    <Pressable
                      onPress={() => handleConfirm(s.share_id, s.item_name)}
                      disabled={isBusy}
                      style={({ pressed }) => [
                        styles.pendingConfirmBtn,
                        pressed && { opacity: 0.85 },
                        isBusy && { opacity: 0.6 },
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel={`Confirm receipt of ${s.item_name}`}
                      testID={`pending-confirm-btn-${s.share_id}`}
                    >
                      {isBusy ? (
                        <ActivityIndicator size="small" color={fpColors.onPrimary} />
                      ) : (
                        <>
                          <MaterialIcons
                            name="check"
                            size={14}
                            color={fpColors.onPrimary}
                          />
                          <Text style={styles.pendingConfirmText}>Confirm</Text>
                        </>
                      )}
                    </Pressable>
                  </View>
                );
              })}
            </View>
          </View>
        ) : null}

        {visible.length === 0 ? (
          <View style={styles.emptyState}>
            {isLoading ? (
              <ActivityIndicator color={fpColors.primary} />
            ) : (
              <>
                <MaterialIcons
                  name={tab === 'received' ? 'inbox' : 'outbox'}
                  size={36}
                  color={fpColors.outline}
                />
                <Text style={styles.emptyText}>
                  {tab === 'received'
                    ? 'Nothing has been shared with you yet.'
                    : "You haven't shared anything yet."}
                </Text>
              </>
            )}
          </View>
        ) : (
          <View style={styles.card}>
            {visible.map((s) =>
              tab === 'received' ? (
                <ShareRow
                  key={s.share_id}
                  shareId={s.share_id}
                  itemName={s.item_name}
                  status={s.status}
                  sharingType={s.sharing_type}
                  createdAt={s.created_at}
                  expectedReturnDate={s.expected_return_date}
                  counterparty={(s as SharedWithMe).shared_by}
                  direction="inbound"
                  onPress={() => {
                    // PR19 — symmetric with the Sent tab below.
                    // PENDING (not yet claimed) → routes to the
                    // ShareClaim screen so the user can claim.
                    // CLAIMED / RETURNED / COMPLETED → routes to
                    // ItemDetail of the inbound row's item_id (BE#179)
                    // so the borrower lands on the row they hold.
                    if (s.status === 'PENDING') {
                      goShareClaim((s as any).share_url);
                      return;
                    }
                    const itemId = (s as any).item_id as number | null;
                    if (itemId != null) {
                      navigation.navigate('ItemDetail', { itemId });
                    }
                  }}
                  // C5 — borrower marks an active borrow returned. Only the
                  // claimer of a still-CLAIMED inbound row can do this.
                  onAction={
                    s.status === 'CLAIMED'
                      ? () => handleReturn(s.share_id, s.item_name)
                      : undefined
                  }
                  actionLabel={s.status === 'CLAIMED' ? 'Return' : undefined}
                  actionIcon={
                    s.status === 'CLAIMED' ? 'assignment-return' : undefined
                  }
                  actionBusy={returningShareId === s.share_id}
                  actionTestID={`share-return-${s.share_id}`}
                />
              ) : (
                <ShareRow
                  key={s.share_id}
                  shareId={s.share_id}
                  itemName={s.item_name}
                  status={s.status}
                  sharingType={s.sharing_type}
                  createdAt={s.created_at}
                  expectedReturnDate={s.expected_return_date}
                  counterparty={(s as SharedByMe).shared_with}
                  direction="outbound"
                  onPress={() => {
                    // PR19 — Open the underlying item directly.
                    // BE#179 ships item_id on SharedByMe rows so we
                    // can navigate without first hitting MyShares.
                    // Legacy rows (item_id == null) fall through to
                    // a no-op rather than risk a wrong navigation.
                    const itemId = (s as any).item_id as number | null;
                    if (itemId != null) {
                      navigation.navigate('ItemDetail', { itemId });
                    }
                  }}
                  // C4 — owner nudges a borrower whose loan is still open.
                  // CLAIMED = out with someone; RETURNED moves to the
                  // "Awaiting your confirmation" section above instead.
                  onAction={
                    s.status === 'CLAIMED'
                      ? () => handleRemind(s.share_id, s.item_name)
                      : undefined
                  }
                  actionLabel={s.status === 'CLAIMED' ? 'Remind' : undefined}
                  actionIcon={
                    s.status === 'CLAIMED' ? 'notifications' : undefined
                  }
                  actionBusy={remindingShareId === s.share_id}
                  actionTestID={`share-remind-${s.share_id}`}
                />
              ),
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const TabButton: React.FC<{
  label: string;
  active: boolean;
  onPress: () => void;
  testID?: string;
}> = ({ label, active, onPress, testID }) => (
  <Pressable
    onPress={onPress}
    testID={testID}
    accessibilityRole="tab"
    accessibilityState={{ selected: active }}
    style={({ pressed }) => [
      styles.tabBtn,
      active && styles.tabBtnActive,
      pressed && { opacity: 0.85 },
    ]}
  >
    <Text style={[styles.tabLabel, active && styles.tabLabelActive]}>
      {label}
    </Text>
  </Pressable>
);

const FilterChip: React.FC<{
  label: string;
  active: boolean;
  onPress: () => void;
}> = ({ label, active, onPress }) => (
  <Pressable
    onPress={onPress}
    style={({ pressed }) => [
      styles.chip,
      active && styles.chipActive,
      pressed && { opacity: 0.85 },
    ]}
    accessibilityRole="button"
    accessibilityState={{ selected: active }}
  >
    <Text style={[styles.chipLabel, active && styles.chipLabelActive]}>
      {label}
    </Text>
  </Pressable>
);

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    height: Platform.OS === 'ios' ? 104 : 68,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  tabRow: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingTop: 10,
    gap: 8,
  },
  tabBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
  },
  tabBtnActive: {
    backgroundColor: fpColors.primary,
  },
  tabLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  tabLabelActive: {
    color: fpColors.onPrimary,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 6,
    gap: 6,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  chipActive: {
    backgroundColor: fpColors.primaryContainer,
    borderColor: fpColors.primary,
  },
  chipLabel: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  chipLabelActive: {
    color: fpColors.onPrimaryContainer,
  },
  list: { padding: 14, paddingTop: 6, gap: 14 },
  card: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 14,
    overflow: 'hidden',
  },
  emptyState: {
    paddingVertical: 64,
    alignItems: 'center',
    gap: 12,
  },
  emptyText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    paddingHorizontal: 40,
  },
  // PR12 — Awaiting your confirmation section.
  pendingSection: {
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 14,
    backgroundColor: fpColors.tertiaryContainer,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: fpColors.tertiary,
  },
  pendingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  pendingTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onTertiaryContainer ?? fpColors.tertiary,
    letterSpacing: 0.2,
  },
  pendingSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 4,
    marginBottom: 10,
  },
  pendingList: { gap: 8 },
  pendingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    backgroundColor: fpColors.surface,
    borderRadius: 10,
  },
  pendingRowText: { flex: 1, minWidth: 0 },
  pendingItemName: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  pendingItemSub: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  pendingConfirmBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: fpColors.primary,
    borderRadius: 999,
    minWidth: 88,
    justifyContent: 'center',
  },
  pendingConfirmText: {
    color: fpColors.onPrimary,
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    letterSpacing: 0.3,
  },
});
